import { forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { TextArea } from '@shared/components/ui/textarea';
import { RateItem, RateItemFormRef, StorageData } from '../../models/rate.types';
import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';

const storageFormSchema = z.object({
  notes: z.string().optional(),
  fixed_fee: z.string().min(1, 'validation.feePositive'),
});

type StorageFormData = z.infer<typeof storageFormSchema>;

interface AddStorageItemFormProps {
  initialData?: RateItem;
}

export const AddStorageItemForm = forwardRef<RateItemFormRef, AddStorageItemFormProps>(({ initialData }, ref) => {
  const { t } = useTranslation('ratesModule');
  const currency = useCurrency();

  const storageData = initialData?.data as StorageData | undefined;

  const {
    control,
    trigger,
    getValues,
    formState: { errors },
  } = useForm<StorageFormData>({
    resolver: zodResolver(storageFormSchema),
    mode: 'onChange',
    defaultValues: {
      notes: initialData?.notes || '',
      fixed_fee: storageData?.fixed_fee != null ? String(storageData.fixed_fee) : '',
    },
  });

  useImperativeHandle(
    ref,
    () => ({
      getData: async () => {
        const isValid = await trigger();
        if (!isValid) return null;

        const values = getValues();
        const fixedFee = parseFloat(values.fixed_fee);
        if (isNaN(fixedFee)) return null;

        return {
          title: t('itemTypes.storage'),
          type: 'storage' as const,
          notes: values.notes || null,
          data: {
            fixed_fee: fixedFee,
          },
        };
      },
    }),
    [trigger, getValues, t],
  );

  return (
    <div className="space-y-4">
      <div>
        <Label>{t('form.storage.fixedFee', { currency })} *</Label>
        <Controller
          name="fixed_fee"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              className={errors.fixed_fee ? 'border-error' : ''}
            />
          )}
        />
        {errors.fixed_fee && <p className="text-xs text-error mt-1">{t(errors.fixed_fee.message!)}</p>}
      </div>

      <div>
        <Label>{t('form.notes')}</Label>
        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <TextArea {...field} value={field.value || ''} placeholder={t('form.notesPlaceholder')} rows={2} />
          )}
        />
      </div>
    </div>
  );
});

AddStorageItemForm.displayName = 'AddStorageItemForm';
